// Time picker component with single-column dropdown (UX optimized)
// REDESIGNED: Simple time slot selection with one-click interaction
// Features: 15-minute intervals, auto-scroll to 09:00 by default, format "08:30"
// Optimized for users uncomfortable with complex UIs - maximum simplicity
// UPDATED: Added time format normalization for auto-scroll to work with various time formats from database

import { useState, useRef, useEffect } from 'react';
import { Clock, X } from 'lucide-react';
import { normalizeTimeFormat } from '../../lib/dateUtils';

interface TimePickerProps {
  value: string | null;
  onChange: (time: string) => void;
  placeholder?: string;
  step?: number;
  disabled?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
}

export function TimePicker({
  value,
  onChange,
  placeholder = 'Sélectionnez une heure',
  step = 15,
  disabled = false,
  className = '',
  label,
  required = false
}: TimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');

  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Format number to 2 digits
  const formatNumber = (num: number): string => num.toString().padStart(2, '0');

  // Generate all time slots with 15-minute intervals (00:00 to 23:45)
  const generateTimeSlots = (): string[] => {
    const slots: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += step) {
        slots.push(`${formatNumber(hour)}:${formatNumber(minute)}`);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Calculate optimal dropdown position and auto-scroll
  useEffect(() => {
    if (isOpen && dropdownRef.current && listRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const dropdownHeight = 320;

      // Check vertical space
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const position = spaceBelow >= dropdownHeight || spaceBelow > spaceAbove ? 'bottom' : 'top';

      setDropdownPosition(position);

      // Auto-scroll to selected value or default to 09:00
      setTimeout(() => {
        // Normalize the time format to ensure it matches the data-time attributes (HH:MM)
        // This handles cases where the database returns "9:00:00", "09:00:00", etc.
        const normalizedValue = value ? normalizeTimeFormat(value) : '';
        const targetTime = normalizedValue || '09:00';
        const selectedElement = listRef.current?.querySelector(`[data-time="${targetTime}"]`);

        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } else {
          // Fallback: scroll to 09:00 if value not found
          const defaultElement = listRef.current?.querySelector(`[data-time="09:00"]`);
          defaultElement?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 50);
    }
  }, [isOpen, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen]);

  const handleTimeSelect = (time: string) => {
    onChange(time);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
  };

  const displayValue = value || placeholder;
  const hasValue = !!value;

  // Normalize value for comparison with time slots
  const normalizedValue = value ? normalizeTimeFormat(value) : '';

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center gap-3 px-4 py-2 border rounded-lg transition-all text-sm ${
          disabled
            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
            : hasValue
            ? 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none'
            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none'
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label || 'Sélectionner une heure'}
      >
        <Clock size={18} className={hasValue ? 'text-gray-600' : 'text-gray-400'} />
        <span className="flex-1 text-left">{displayValue}</span>
        {hasValue && !disabled && (
          <X
            size={18}
            className="text-gray-400 hover:text-gray-600"
            onClick={handleClear}
          />
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute left-0 w-full bg-white border border-gray-200 rounded-lg shadow-xl z-[60] ${
            dropdownPosition === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-700">Sélectionner l'heure</span>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Fermer"
            >
              <X size={18} />
            </button>
          </div>

          {/* Single Time Slot List */}
          <div
            ref={listRef}
            className="overflow-y-auto"
            style={{ maxHeight: '280px' }}
            role="listbox"
            aria-label="Créneaux horaires"
          >
            {timeSlots.map((time) => (
              <button
                key={time}
                type="button"
                data-time={time}
                onClick={() => handleTimeSelect(time)}
                className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                  normalizedValue === time
                    ? 'bg-primary-500 text-white font-semibold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                role="option"
                aria-selected={normalizedValue === time}
              >
                {time}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
